import React, { useState, useEffect, useRef } from 'react';
import {
  MapPin,
  Truck,
  Navigation,
  Clock,
  Phone,
  User,
  RefreshCw,
  X,
  ChevronRight,
  Package,
  AlertCircle
} from 'lucide-react';
import { supabase } from '../supabaseClient';

const DriverLocationMap = ({ storeId, onClose }) => {
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapError, setMapError] = useState(null);
  const [leafletLoaded, setLeafletLoaded] = useState(!!window.L);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});

  // Dynamically load Leaflet if not already loaded
  useEffect(() => {
    if (window.L) {
      setLeafletLoaded(true);
      return;
    }

    // Load Leaflet CSS
    const linkEl = document.createElement('link');
    linkEl.rel = 'stylesheet';
    linkEl.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    linkEl.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
    linkEl.crossOrigin = '';
    document.head.appendChild(linkEl);

    // Load Leaflet JS
    const scriptEl = document.createElement('script');
    scriptEl.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    scriptEl.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
    scriptEl.crossOrigin = '';
    scriptEl.onload = () => {
      console.log('✅ Leaflet loaded dynamically');
      setLeafletLoaded(true);
    };
    scriptEl.onerror = () => {
      console.error('❌ Failed to load Leaflet');
      setMapError('Failed to load map library');
    };
    document.head.appendChild(scriptEl);

    return () => {
      // Cleanup if component unmounts before load
    };
  }, []);

  // Load active routes with driver locations
  useEffect(() => {
    loadActiveRoutes();
    
    // Subscribe to real-time updates for both route changes and location updates
    const channel = supabase
      .channel('driver-locations')
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'delivery_routes' },
        (payload) => {
          console.log('🚚 Route update:', payload.new);
          // Only update route info, not location
          updateRouteInState(payload.new);
        }
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'driver_locations' },
        (payload) => {
          console.log('📍 Driver location update:', payload.new);
          // Update the location for this route
          if (payload.new && payload.new.route_id) {
            setRoutes(prev => prev.map(r => {
              if (r.id === payload.new.route_id) {
                return {
                  ...r,
                  driver_latitude: payload.new.latitude,
                  driver_longitude: payload.new.longitude,
                  driver_accuracy: payload.new.accuracy,
                  driver_heading: payload.new.heading,
                  driver_speed: payload.new.speed,
                  driver_location_updated_at: payload.new.updated_at
                };
              }
              return r;
            }));
          }
        }
      )
      .subscribe();

    // Refresh every 30 seconds as backup
    const interval = setInterval(loadActiveRoutes, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, [storeId]);

  // Initialize map when Leaflet is ready (don't wait for routes)
  useEffect(() => {
    if (leafletLoaded && mapRef.current && !mapInstanceRef.current) {
      initializeMap();
    }
  }, [leafletLoaded]);

  // Update markers when routes change
  useEffect(() => {
    if (mapInstanceRef.current && routes.length > 0) {
      updateMarkers();
    }
  }, [routes]);

  const loadActiveRoutes = async () => {
    try {
      setLoading(true);
      console.log('🗺️ Loading active routes for store:', storeId);
      
      // First get routes with stops
      let query = supabase
        .from('delivery_routes')
        .select(`
          *,
          delivery_route_stops (*)
        `)
        .in('status', ['planned', 'dispatched', 'in_progress'])
        .order('created_at', { ascending: false });

      if (storeId) {
        query = query.eq('store_id', storeId);
      }

      const { data: routesData, error } = await query;

      if (error) {
        console.error('❌ Error loading routes:', error);
        throw error;
      }
      
      // Now get driver locations from the separate table
      const routeIds = routesData?.map(r => r.id) || [];
      let routesWithLocations = routesData || [];
      
      if (routeIds.length > 0) {
        const { data: locationsData, error: locError } = await supabase
          .from('driver_locations')
          .select('*')
          .in('route_id', routeIds);
        
        if (!locError && locationsData) {
          // Merge location data into routes
          const locationMap = {};
          locationsData.forEach(loc => {
            locationMap[loc.route_id] = loc;
          });
          
          routesWithLocations = routesData.map(route => {
            const loc = locationMap[route.id];
            if (loc) {
              return {
                ...route,
                driver_latitude: loc.latitude,
                driver_longitude: loc.longitude,
                driver_accuracy: loc.accuracy,
                driver_heading: loc.heading,
                driver_speed: loc.speed,
                driver_location_updated_at: loc.updated_at
              };
            }
            return route;
          });
        }
      }
      
      console.log('📍 Routes loaded:', routesWithLocations?.length || 0);
      if (routesWithLocations && routesWithLocations.length > 0) {
        console.log('📍 Route details:', routesWithLocations.map(r => ({
          id: r.id?.slice(0, 8),
          status: r.status,
          driver: r.driver_name,
          hasLocation: !!(r.driver_latitude && r.driver_longitude),
          lat: r.driver_latitude,
          lng: r.driver_longitude,
          updatedAt: r.driver_location_updated_at
        })));
      }

      setRoutes(routesWithLocations || []);
    } catch (err) {
      console.error('Error loading routes:', err);
      setMapError('Failed to load driver locations');
    } finally {
      setLoading(false);
    }
  };

  const updateRouteInState = (updatedRoute) => {
    setRoutes(prev => prev.map(r => 
      r.id === updatedRoute.id ? { ...r, ...updatedRoute } : r
    ));
  };

  const initializeMap = () => {
    // Using Leaflet via CDN (add to index.html):
    // <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
    // <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
    
    if (!window.L) {
      setMapError('Map library not loaded. Please refresh the page.');
      return;
    }

    try {
      // Find center point from all routes
      const routesWithLocation = routes.filter(r => r.driver_latitude && r.driver_longitude);
      
      let centerLat = 38.5449;  // Default to Davis, CA
      let centerLng = -121.7405;

      if (routesWithLocation.length > 0) {
        centerLat = routesWithLocation.reduce((sum, r) => sum + r.driver_latitude, 0) / routesWithLocation.length;
        centerLng = routesWithLocation.reduce((sum, r) => sum + r.driver_longitude, 0) / routesWithLocation.length;
      }

      // Initialize map
      const map = window.L.map(mapRef.current).setView([centerLat, centerLng], 12);

      // Add tile layer (OpenStreetMap - free)
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(map);

      mapInstanceRef.current = map;
      updateMarkers();
    } catch (err) {
      console.error('Map initialization error:', err);
      setMapError('Failed to initialize map');
    }
  };

  const updateMarkers = () => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Clear old markers
    Object.values(markersRef.current).forEach(marker => map.removeLayer(marker));
    markersRef.current = {};

    // Add markers for each route with location
    routes.forEach(route => {
      if (!route.driver_latitude || !route.driver_longitude) return;

      // Custom truck icon - using PNG image for cross-platform compatibility
      const truckIcon = window.L.icon({
        iconUrl: '/3.png',
        iconSize: [40, 40],
        iconAnchor: [20, 20],
        popupAnchor: [0, -20]
      });

      const marker = window.L.marker([route.driver_latitude, route.driver_longitude], {
        icon: truckIcon
      }).addTo(map);

      // Add popup
      const completedStops = route.delivery_route_stops?.filter(s => s.status === 'delivered').length || 0;
      const totalStops = route.delivery_route_stops?.length || 0;
      const lastUpdate = route.driver_location_updated_at 
        ? new Date(route.driver_location_updated_at).toLocaleTimeString()
        : 'Unknown';

      marker.bindPopup(`
        <div style="min-width: 200px;">
          <strong style="font-size: 14px; color: #0f172a;">${route.driver_name || 'Driver'}</strong>
          <br/>
          <span style="color: #64748b; font-size: 12px;">${route.route_name || 'Route'}</span>
          <hr style="margin: 8px 0; border: none; border-top: 1px solid #e2e8f0;"/>
          <div style="font-size: 12px; color: #475569;">
            <div>📍 ${completedStops}/${totalStops} stops completed</div>
            <div>🕐 Updated: ${lastUpdate}</div>
            ${route.driver_speed ? `<div>🚗 ${Math.round(route.driver_speed * 3.6)} km/h</div>` : ''}
          </div>
        </div>
      `);

      marker.on('click', () => setSelectedRoute(route));

      markersRef.current[route.id] = marker;

      // Also add markers for pending stops
      route.delivery_route_stops?.forEach((stop, index) => {
        if (stop.status === 'delivered') return;
        if (!stop.latitude || !stop.longitude) return;

        const stopIcon = window.L.divIcon({
          className: 'stop-marker',
          html: `
            <div style="
              background: ${stop.status === 'in_progress' ? '#f59e0b' : '#64748b'};
              border-radius: 50%;
              width: 30px;
              height: 30px;
              display: flex;
              align-items: center;
              justify-content: center;
              border: 2px solid white;
              box-shadow: 0 2px 6px rgba(0,0,0,0.2);
              color: white;
              font-weight: bold;
              font-size: 12px;
            ">
              ${stop.stop_order || index + 1}
            </div>
          `,
          iconSize: [30, 30],
          iconAnchor: [15, 15]
        });

        const stopMarker = window.L.marker([stop.latitude, stop.longitude], {
          icon: stopIcon
        }).addTo(map);

        stopMarker.bindPopup(`
          <div style="min-width: 180px;">
            <strong style="color: #0f172a;">${stop.customer_name || 'Customer'}</strong>
            <br/>
            <span style="color: #64748b; font-size: 12px;">${stop.address || 'No address'}</span>
            ${stop.eta_minutes ? `<br/><span style="color: #2563eb; font-weight: 500;">ETA: ~${stop.eta_minutes} min</span>` : ''}
          </div>
        `);
      });
    });

    // Fit bounds if we have markers
    const routesWithLocation = routes.filter(r => r.driver_latitude && r.driver_longitude);
    if (routesWithLocation.length > 0) {
      const bounds = routesWithLocation.map(r => [r.driver_latitude, r.driver_longitude]);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  };

  const getTimeSinceUpdate = (timestamp) => {
    if (!timestamp) return 'No updates';
    const seconds = Math.floor((new Date() - new Date(timestamp)) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  return (
    <div className="fixed inset-0 bg-slate-100 z-50 flex flex-col">
      {/* Header */}
      <div 
        className="p-4 border-b border-slate-200 flex items-center justify-between bg-white shadow-sm"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
            <Truck className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Driver Tracking</h2>
            <p className="text-sm text-slate-500">
              {routes.length} active route{routes.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadActiveRoutes}
            className="p-2.5 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-xl text-slate-500 hover:text-slate-700 transition-all"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={onClose}
            className="p-2.5 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-xl text-slate-500 hover:text-slate-700 transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-y-auto lg:overflow-hidden">
        {/* Map - Full width on mobile */}
        <div className="relative h-[50vh] lg:h-auto lg:flex-1 flex-shrink-0">
          {!leafletLoaded && !mapError ? (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
              <div className="text-center">
                <RefreshCw className="w-12 h-12 text-blue-600 mx-auto mb-3 animate-spin" />
                <p className="text-slate-900 font-semibold">Loading map...</p>
                <p className="text-slate-500 text-sm mt-1">
                  Please wait while the map library loads
                </p>
              </div>
            </div>
          ) : mapError ? (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
              <div className="text-center px-4">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                <p className="text-slate-900 font-semibold">{mapError}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-4 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm font-semibold hover:bg-blue-100 transition-all"
                >
                  Refresh Page
                </button>
              </div>
            </div>
          ) : (
            <div ref={mapRef} className="w-full h-full" />
          )}

          {/* No active routes message - overlay on map */}
          {!loading && leafletLoaded && routes.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/90 z-10 pointer-events-none">
              <div className="text-center pointer-events-auto px-4">
                <Truck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">No Active Deliveries</h3>
                <p className="text-slate-500 max-w-xs">
                  Driver locations will appear here when routes are dispatched and drivers share their location
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Route List - Bottom sheet on mobile, sidebar on desktop */}
        <div className="flex-shrink-0 lg:w-80 bg-white border-t lg:border-t-0 lg:border-l border-slate-200 lg:overflow-y-auto lg:max-h-none">
          <div className="p-4 border-b border-slate-200 sticky top-0 bg-white z-10">
            <h3 className="text-sm font-medium text-slate-500">
              Active Routes
            </h3>
          </div>

          {routes.length === 0 ? (
            <div className="p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <Truck className="w-6 h-6 text-slate-400" />
              </div>
              <p className="text-slate-500 text-sm">No active routes</p>
            </div>
          ) : (
          <div className="divide-y divide-slate-100">
            {routes.map(route => {
              const completedStops = route.delivery_route_stops?.filter(s => s.status === 'delivered').length || 0;
              const totalStops = route.delivery_route_stops?.length || 0;
              const hasLocation = route.driver_latitude && route.driver_longitude;
              
              return (
                <button
                  key={route.id}
                  onClick={() => {
                    setSelectedRoute(route);
                    if (hasLocation && mapInstanceRef.current) {
                      mapInstanceRef.current.setView([route.driver_latitude, route.driver_longitude], 14);
                    }
                  }}
                  className={`w-full p-4 text-left hover:bg-slate-50 transition-all ${
                    selectedRoute?.id === route.id ? 'bg-blue-50 border-l-2 border-blue-600' : 'border-l-2 border-transparent'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center border ${
                        hasLocation 
                          ? 'bg-emerald-50 border-emerald-200' 
                          : 'bg-slate-100 border-slate-200'
                      }`}>
                        <Truck className={`w-4 h-4 ${hasLocation ? 'text-emerald-600' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{route.driver_name || 'Driver'}</p>
                        <p className="text-xs text-slate-500">{route.route_name || 'Route'}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>

                  {/* Progress */}
                  <div className="mb-2">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-500">{completedStops}/{totalStops} stops</span>
                      <span className="text-blue-600 font-medium">
                        {totalStops > 0 ? Math.round((completedStops/totalStops)*100) : 0}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all"
                        style={{ width: `${totalStops > 0 ? (completedStops/totalStops)*100 : 0}%` }}
                      />
                    </div>
                  </div>

                  {/* Location status */}
                  <div className="flex items-center gap-2 text-xs">
                    {hasLocation ? (
                      <>
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        <span className="text-emerald-600 font-medium">
                          Live • {getTimeSinceUpdate(route.driver_location_updated_at)}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                        <span className="text-slate-500">No location</span>
                      </>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          )}

          {/* Selected Route Details */}
          {selectedRoute && (
            <div className="p-4 border-t border-slate-200 bg-slate-50">
              <h4 className="font-medium text-slate-900 text-sm mb-3">Upcoming Stops</h4>
              <div className="space-y-2">
                {selectedRoute.delivery_route_stops
                  ?.filter(s => s.status !== 'delivered')
                  .slice(0, 3)
                  .map((stop, index) => (
                    <div key={stop.id} className="flex items-center gap-3 p-2.5 bg-white border border-slate-200 rounded-xl shadow-sm">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${
                        index === 0 
                          ? 'bg-blue-100 border border-blue-200 text-blue-700' 
                          : 'bg-slate-100 text-slate-500'
                      }`}>
                        {stop.stop_order || index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-900 truncate">{stop.customer_name}</p>
                        {stop.eta_minutes && (
                          <p className="text-xs text-blue-600 font-medium">ETA: ~{stop.eta_minutes} min</p>
                        )}
                      </div>
                    </div>
                  ))}
              </div>

              {/* Driver contact */}
              {selectedRoute.driver_phone && (
                <a
                  href={`tel:${selectedRoute.driver_phone}`}
                  className="mt-3 flex items-center justify-center gap-2 w-full py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm font-medium hover:bg-emerald-100 transition-all"
                >
                  <Phone className="w-4 h-4" />
                  Call Driver
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Map CSS - Light Mode */}
      <style>{`
        .leaflet-container {
          background: #f1f5f9;
        }
        .driver-marker, .stop-marker {
          background: none !important;
          border: none !important;
        }
        .truck-marker-icon {
          background: linear-gradient(135deg, #2563eb, #3b82f6) !important;
          border-radius: 50% !important;
          width: 40px !important;
          height: 40px !important;
          border: 3px solid white !important;
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3) !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        .truck-marker-icon svg {
          width: 20px !important;
          height: 20px !important;
          fill: white !important;
        }
        .leaflet-popup-content-wrapper {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          color: #0f172a;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        .leaflet-popup-tip {
          background: white;
          border: 1px solid #e2e8f0;
        }
        .leaflet-control-zoom {
          border: 1px solid #e2e8f0 !important;
          border-radius: 12px !important;
          overflow: hidden;
          box-shadow: 0 2px 6px rgba(0,0,0,0.1) !important;
        }
        .leaflet-control-zoom a {
          background: white !important;
          color: #475569 !important;
          border-color: #e2e8f0 !important;
        }
        .leaflet-control-zoom a:hover {
          background: #f1f5f9 !important;
          color: #0f172a !important;
        }
        .leaflet-control-attribution {
          background: rgba(255,255,255,0.9) !important;
          color: #64748b !important;
        }
      `}</style>
    </div>
  );
};

export default DriverLocationMap;